// Category data for SEO landing pages
export interface SubcollectionConfig {
  id: string // url/state-safe id, e.g. "nintendo"
  label: string // user-facing tab label, e.g. "Nintendo"
  // Case-insensitive substrings matched against tags, title, description.
  // A sound matches the subcollection if ANY keyword matches.
  keywords: string[]
}

export type HeroRegister = 'tight' | 'wide' | 'display' | 'urgent' | 'default'

export type CategoryFamily = 'warm' | 'cool' | 'neutral'

const FAMILY_BY_SLUG: Record<string, CategoryFamily> = {
  memes: 'warm',
  viral: 'warm',
  movies: 'warm',
  'tv-shows': 'warm',
  sports: 'warm',
  trending: 'warm',
  reactions: 'warm',
  'cartoons-animation': 'warm',
  'internet-culture': 'warm',
  'meme-classics': 'warm',
  music: 'cool',
  'nature-animals': 'cool',
  'sci-fi-futuristic': 'cool',
  'sci-fi': 'cool',
  games: 'neutral',
  'retro-gaming': 'neutral',
  'modern-gaming': 'neutral',
  'retro-tech': 'neutral',
  'horror-spooky': 'neutral',
  'sound-effects': 'neutral',
  // TES-1834 hubs
  anime: 'warm',
  'adult-animation': 'warm',
  'spongebob-nickelodeon': 'warm',
  'game-shows-quiz-tv': 'warm',
  'pro-wrestling-mma': 'warm',
  'soccer-football': 'warm',
  'sci-fi-action-films': 'warm',
  'jingles-ringtones': 'cool',
  'hip-hop-r-b': 'cool',
  'discord-app-sounds': 'neutral',
  'vehicles-transport': 'neutral',
  'f1-motorsport': 'neutral',
}

export function getCategoryFamily(slug: string): CategoryFamily {
  return FAMILY_BY_SLUG[slug] ?? 'neutral'
}

export interface CategoryInfo {
  slug: string
  name: string
  title: string
  description: string
  metaDescription: string
  heroText: string
  icon: string // lucide icon name
  relatedCategories: string[]
  features: string[]
  // Optional client-side filter tabs for large coherent categories that
  // shouldn't be split via taxonomy. Rendered as tabs above the grid.
  subcollections?: SubcollectionConfig[]
  // Sound detail page variation fields (TES-821 / TES-810 §7)
  /**
   * Per-category hero typography register.
   *
   * CURRENTLY UNCONSUMED. Plate & Hairline gives the title one treatment that
   * the plate owns, so nothing reads this today. Do not wire it back into the
   * heading without reading the note in app/sounds/[id]/sound-player-client.tsx:
   * the `wide` and `urgent` registers move tracking by about 0.1em, and the
   * plate solver's advance-width constant is calibrated at the plate's -0.05em,
   * so honouring them makes the server pass solve for a string that will not
   * render.
   *
   * Kept because it records real design intent, and a register that varies
   * something the plate does not own would be free to use it.
   */
  heroRegister?: HeroRegister
  shareCopy?: string
  accentToken?: string
}

export const categoryData: Record<string, CategoryInfo> = {
  'games': {
    slug: 'games',
    name: 'Games',
    title: 'Gaming Tesla Lock Sounds',
    description: 'Iconic gaming sounds for your Tesla: retro 8-bit chimes, Roblox, FNAF, and more.',
    metaDescription: 'Download free gaming lock sounds for your Tesla. Retro, arcade, and modern video game audio for your vehicle.',
    heroText: 'Player One Ready',
    icon: 'Gamepad2',
    relatedCategories: ['memes', 'viral', 'movies'],
    features: ['Retro chiptunes', 'Achievement sounds', 'Power-up effects', 'Arcade classics'],
    heroRegister: 'tight',
    shareCopy: 'Your Tesla just earned an achievement.',
    accentToken: '--accent-category-games',
  },
  'memes': {
    slug: 'memes',
    name: 'Memes',
    title: 'Meme Tesla Lock Sounds',
    description: 'Popular meme sounds for your Tesla: Vine Boom, Emotional Damage, and the rest of the catalog.',
    metaDescription: 'Download free meme lock sounds for your Tesla. Viral internet sounds, funny memes, and iconic audio clips.',
    heroText: 'Maximum Meme Mode',
    icon: 'Laugh',
    relatedCategories: ['viral', 'games', 'movies'],
    features: ['Vine classics', 'TikTok sounds', 'Reaction memes', 'Internet culture'],
    heroRegister: 'tight',
    shareCopy: 'Show your group chat what a Tesla can really do.',
    accentToken: '--accent-category-memes',
  },
  // 'movies-tv' retired in TES-781 — slug 301s to /sounds/category/movies via
  // next.config.ts. Removed here so generateStaticParams and the sitemap stop
  // listing the redirected URL.
  'music': {
    slug: 'music',
    name: 'Music',
    title: 'Music Lock Sounds for Tesla: Bass Drops, Riffs & Jingles (Free WAV)',
    description: 'Bass drops, funk riffs, iconic jingles, vocal hooks: all Tesla-format WAV, USB-ready. Michael Jackson\'s Hee-Hee, James Brown, Careless Whisper saxophone.',
    metaDescription: 'Free music lock sounds for Tesla. Bass drops, riffs, jingles & vocal hooks, tesla-format WAV for Model 3, Y, S, X & Cybertruck. USB install, no conversion.',
    heroText: 'Drop the Beat',
    icon: 'Music',
    relatedCategories: ['viral', 'movies', 'memes'],
    features: ['Bass drops & wubs', 'Funk & soul riffs', 'Iconic jingles', 'Vocal hooks'],
    heroRegister: 'display',
    shareCopy: 'Let your driveway drop the bass.',
    accentToken: '--accent-category-music',
  },
  'reactions': {
    slug: 'reactions',
    name: 'Reactions',
    title: 'Reaction Lock Sounds for Tesla: Gasp, Bruh, Crowd Roar & OMG (Free WAV)',
    description: 'Gasp, cheer, bruh, oh no: reaction sounds for your Tesla lock. Surprised screams, crowd pops, "WHAT?!", dramatic pauses, and the full OMG repertoire. Tesla-format WAV, USB-ready.',
    metaDescription: 'Free reaction lock sounds for Tesla: gasps, crowd roars, bruh, OMG, oh no. Expressive audio chimes in Tesla-format WAV for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Big Reaction Energy',
    icon: 'MessageCircle',
    relatedCategories: ['memes', 'internet-culture', 'sports'],
    features: ['Gasps & screams', 'Crowd pops & cheers', 'Bruh moments', 'OMG reactions'],
    heroRegister: 'default',
    shareCopy: 'Your Tesla has opinions about your parking.',
    accentToken: '--accent-category-default',
    subcollections: [
      {
        id: 'shock-gasp',
        label: 'Shock & Gasp',
        keywords: ['gasp', 'scream', 'oh no', 'uh oh', 'scared', 'dramatic', 'oh my god', 'what the', 'no no no', 'horror'],
      },
      {
        id: 'crowd-hype',
        label: 'Crowd & Hype',
        keywords: ['crowd', 'cheer', 'applause', 'hype', 'wooo', 'audience', 'stadium', 'clap', 'roar', 'let\'s go'],
      },
      {
        id: 'viral-reactions',
        label: 'Bruh & Viral',
        keywords: ['bruh', 'vine', 'fail', 'OMG', 'omg', 'seriously', 'really', 'awkward', 'classic', 'internet'],
      },
    ],
  },
  'sports': {
    slug: 'sports',
    name: 'Sports',
    title: 'Sports Tesla Lock Sounds',
    description: 'Crowd cheers, referee whistles, goal horns: sports audio for your Tesla lock.',
    metaDescription: 'Download free sports lock sounds for your Tesla. Stadium crowds, whistles, and athletic audio.',
    heroText: 'Game Day Ready',
    icon: 'Trophy',
    relatedCategories: ['memes', 'music', 'viral'],
    features: ['Crowd cheers', 'Referee whistles', 'Victory sounds', 'Goal horns'],
    heroRegister: 'urgent',
    shareCopy: 'Your Tesla just scored.',
    accentToken: '--accent-category-default',
  },
  'trending': {
    slug: 'trending',
    name: 'Trending',
    title: 'Trending Tesla Lock Sounds',
    description: 'The hottest lock sounds right now: trending audio clips updated as they break.',
    metaDescription: 'Download trending lock sounds for your Tesla. The hottest and most popular audio right now.',
    heroText: 'What\'s Hot Now',
    icon: 'TrendingUp',
    relatedCategories: ['viral', 'memes', 'games'],
    features: ['Latest hits', 'Popular picks', 'Fresh sounds', 'Hot right now'],
    heroRegister: 'default',
    shareCopy: 'Everyone is doing it. Now your Tesla is too.',
    accentToken: '--accent-category-default',
  },
  'viral': {
    slug: 'viral',
    name: 'Viral',
    title: 'Viral Tesla Lock Sounds: What Everyone Is Using Right Now (Free WAV)',
    description: 'Curated by download velocity: TikTok breakouts, YouTube clips, group-chat favorites. Tesla-format WAV, plug in and lock up.',
    metaDescription: 'Free viral Tesla lock sounds: trending audio clips in Tesla-format WAV for Model 3, Y, S, X & Cybertruck. What everyone is using right now.',
    heroText: 'It\'s Spreading',
    icon: 'Zap',
    relatedCategories: ['memes', 'internet-culture', 'games'],
    features: ['Most downloaded', 'Group chat favorites', 'TikTok breakouts', 'YouTube clips'],
    heroRegister: 'default',
    shareCopy: 'Already in 200 Teslas this week.',
    accentToken: '--accent-category-default',
  },
  'sound-effects': {
    slug: 'sound-effects',
    name: 'Sound Effects',
    title: 'Sound Effect Tesla Lock Sounds',
    description: 'Cartoon boings, dramatic stings, foley classics: the sound-effect shelf for your Tesla lock.',
    metaDescription: 'Download free sound effect lock sounds for your Tesla. Classic foley, cartoon effects, and iconic audio clips.',
    heroText: 'Effect Achieved',
    icon: 'Waves',
    relatedCategories: ['memes', 'games', 'viral'],
    features: ['Cartoon effects', 'Foley sounds', 'Dramatic stings', 'Classic audio'],
    heroRegister: 'default',
    shareCopy: 'Yes, you can actually do this.',
    accentToken: '--accent-category-default',
  },
  'nature-animals': {
    slug: 'nature-animals',
    name: 'Nature & Animals',
    title: 'Nature \u0026 Animals Tesla Lock Sounds',
    description: 'Wolves howling, eagles, thunder, whale songs: organic sounds from the natural world for your Tesla lock.',
    metaDescription: 'Download free nature and animal lock sounds for your Tesla. Wolf howls, eagle cries, thunder, whale songs, and more.',
    heroText: 'Hear the Wild',
    icon: 'Trees',
    relatedCategories: ['sound-effects', 'music', 'movies'],
    features: ['Wolf howls', 'Eagle cries', 'Thunder \u0026 rain', 'Whale songs']
  },
  'retro-tech': {
    slug: 'retro-tech',
    name: 'Retro Tech',
    title: 'Tesla Lock Sound Effects: Notification Chimes, Alert SFX & Retro Tech WAV',
    description: 'Notification chimes, alert SFX, retro tech sounds: Windows XP shutdown, Netflix ta-dum, iPhone click. Tesla-format WAV, USB-ready.',
    metaDescription: 'Free Tesla lock sound effects: notification chimes, alert SFX & retro tech WAV. Windows XP, Netflix, iPhone, startup sounds for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Retro Mode Engaged',
    icon: 'Cpu',
    relatedCategories: ['sci-fi-futuristic', 'games', 'sound-effects'],
    features: ['Notification chimes', 'Startup & shutdown', 'Alert SFX', 'Classic tech audio'],
    heroRegister: 'tight',
    shareCopy: 'Bring 1995 home with you.',
    accentToken: '--accent-category-retro',
  },
  'movies': {
    slug: 'movies',
    name: 'Movies',
    title: 'Movies Tesla Lock Sounds',
    description: 'Iconic movie audio for your Tesla lock: sci-fi effects, action moments, cinematic stings.',
    metaDescription: 'Download free movie lock sounds for your Tesla. Iconic film effects, cinematic audio, and blockbuster sounds.',
    heroText: 'Cinema Mode',
    icon: 'Film',
    relatedCategories: ['tv-shows', 'memes', 'viral'],
    features: ['Sci-fi effects', 'Action sounds', 'Iconic movie quotes', 'Cinematic audio'],
    heroRegister: 'wide',
    shareCopy: 'Make every parking lot feel like a blockbuster.',
    accentToken: '--accent-category-movies',
  },
  'horror-spooky': {
    slug: 'horror-spooky',
    name: 'Horror & Spooky',
    title: 'Horror & Spooky Tesla Lock Sounds',
    description: 'Horror movie stingers, eerie atmospheres, and spine-chilling effects for your Tesla lock.',
    metaDescription: 'Download free horror and spooky lock sounds for your Tesla. Creepy effects, scary stingers, and eerie atmospheric audio.',
    heroText: 'Enter the Dark',
    icon: 'Ghost',
    relatedCategories: ['sci-fi-futuristic', 'sound-effects', 'movies'],
    features: ['Horror stingers', 'Creepy atmosphere', 'Scary sound effects', 'Spooky chimes'],
    heroRegister: 'wide',
    shareCopy: 'They will not see it coming.',
    accentToken: '--accent-category-default',
  },
  'sci-fi-futuristic': {
    slug: 'sci-fi-futuristic',
    name: 'Sci-Fi & Futuristic',
    title: 'Sci-Fi & Futuristic Tesla Lock Sounds',
    description: 'Lightsabers, robot chimes, portal effects: sci-fi and futuristic sounds for your Tesla or Cybertruck.',
    metaDescription: 'Download free sci-fi and futuristic lock sounds for your Tesla. Robot chimes, lightsaber effects, and digital sci-fi audio.',
    heroText: 'Future Mode Activated',
    icon: 'Rocket',
    relatedCategories: ['horror-spooky', 'retro-tech', 'sound-effects'],
    features: ['Robot chimes', 'Lightsaber effects', 'Portal sounds', 'Sci-fi tones'],
    heroRegister: 'wide',
    shareCopy: 'Make every parking lot feel like a hangar.',
    accentToken: '--accent-category-sci-fi',
    subcollections: [
      { id: 'star-wars', label: 'Star Wars', keywords: ['star wars', 'lightsaber', 'vader', 'yoda', 'r2-d2', 'r2d2', 'imperial', 'jedi', 'sith'] },
      { id: 'star-trek', label: 'Star Trek', keywords: ['star trek', 'enterprise', 'spock', 'klingon', 'tricorder'] },
      { id: 'futuristic', label: 'Futuristic', keywords: ['robot', 'cyber', 'portal', 'tron', 'futuristic', 'ai', 'hal', 'transformers'] },
    ],
  },
  'internet-culture': {
    slug: 'internet-culture',
    name: 'Internet Culture',
    title: 'Internet Culture Tesla Lock Sounds',
    description: "Viral memes, TikTok audio, Vine classics, and reaction clips: the internet's greatest hits for your Tesla lock.",
    metaDescription: 'Download free internet culture lock sounds for your Tesla. Memes, TikTok, Vine, viral reactions and more.',
    heroText: 'Extremely Online',
    icon: 'TrendingUp',
    relatedCategories: ['memes', 'viral', 'reactions'],
    features: ['Memes', 'TikTok audio', 'Vine classics', 'Reaction clips'],
    heroRegister: 'tight',
    shareCopy: 'Extremely online. Extremely parked.',
    accentToken: '--accent-category-memes',
    subcollections: [
      {
        id: 'vine-classics',
        label: 'Vine Classics',
        keywords: ['vine', 'vine boom', 'vine app', 'vine era', 'meme', 'memes', 'classic meme', 'early internet', 'rickroll', 'rick astley', 'epic sax', 'why are you running', 'directed by robert', 'spiderman pointing', 'daddy chill', 'omg', 'oh my god meme'],
      },
      {
        id: 'tiktok-modern',
        label: 'TikTok & Modern',
        keywords: ['tiktok', 'tik tok', 'viral', 'gen-z', 'genz', 'brainrot', 'rizz', 'skibidi', 'ohio', 'sigma', 'gyatt', 'hawk tuah', 'delulu', 'slay', 'no cap', 'bussin', 'sus', 'npc', 'mewing', 'looksmaxxing'],
      },
    ],
  },
  'cartoons-animation': {
    slug: 'cartoons-animation',
    name: 'Cartoons & Animation',
    title: 'Cartoons & Animation Tesla Lock Sounds',
    description: 'Looney Tunes, SpongeBob, Pixar: cartoon and animation classics for your Tesla lock.',
    metaDescription: 'Download free cartoon and animation lock sounds for your Tesla. Classic, modern, and animated movie audio.',
    heroText: "That's All, Folks",
    icon: 'Film',
    relatedCategories: ['movies', 'tv-shows', 'memes'],
    features: ['Classic cartoons', 'Modern animation', 'Animated movies', 'Iconic catchphrases'],
    heroRegister: 'display',
    shareCopy: 'Saturday morning energy, every time you lock.',
    accentToken: '--accent-category-animation',
    subcollections: [
      { id: 'classic', label: 'Classic', keywords: ['classic-cartoons', 'looney', 'tom and jerry', 'scooby', 'porky', 'pink panther', 'addams', 'bugs bunny'] },
      { id: 'modern', label: 'Modern', keywords: ['modern-animation', 'spongebob', 'simpsons', 'family guy', 'bluey', 'peppa', 'bratz', 'adult-animation'] },
      { id: 'movies', label: 'Movies', keywords: ['disney-pixar', 'shrek', 'pixar', 'disney', 'dreamworks', 'lego movie', 'frozen', 'baymax'] },
    ],
  },
  'retro-gaming': {
    slug: 'retro-gaming',
    name: 'Retro Gaming',
    title: 'Retro Gaming Tesla Lock Sounds',
    description: '8-bit Nintendo chimes, arcade classics, and PC nostalgia for your Tesla lock.',
    metaDescription: 'Download free retro gaming lock sounds for your Tesla. Nintendo, arcade, 8-bit, and classic console audio.',
    heroText: 'Press Start',
    icon: 'Gamepad2',
    relatedCategories: ['modern-gaming', 'retro-tech', 'sound-effects'],
    features: ['8-bit chiptunes', 'Arcade classics', 'Nintendo jingles', 'Console nostalgia'],
    heroRegister: 'tight',
    shareCopy: 'Press start. Then park.',
    accentToken: '--accent-category-retro',
    subcollections: [
      { id: 'nintendo', label: 'Nintendo', keywords: ['nintendo', 'mario', 'zelda', 'yoshi', 'pikachu', 'pokemon', 'duck hunt', 'gamecube', 'switch'] },
      { id: 'arcade', label: 'Arcade', keywords: ['arcade', 'pac-man', 'pacman', 'mortal kombat', 'street fighter', 'scorpion'] },
      { id: '8bit', label: '8-Bit', keywords: ['8-bit', '16-bit', 'chiptune', 'iconic-jingles'] },
    ],
  },
  'modern-gaming': {
    slug: 'modern-gaming',
    name: 'Modern Gaming',
    title: 'Modern Gaming Tesla Lock Sounds: Fortnite, Halo, Among Us & FNAF (Free WAV)',
    description: 'Current-gen gaming audio for your Tesla lock: Fortnite Victory Royale, Halo Mjolnir, Among Us emergency meeting, FNAF jumpscare, Roblox death sound, GTA wanted-level wail. Tesla-format WAV, USB-ready.',
    metaDescription: 'Free modern gaming lock sounds for Tesla: Fortnite, Halo, Among Us, FNAF, Roblox & GTA in Tesla-format WAV for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Game On',
    icon: 'Gamepad2',
    relatedCategories: ['retro-gaming', 'sci-fi-futuristic', 'sound-effects'],
    features: ['AAA franchises', 'Online multiplayer hits', 'Indie favorites', 'Esports moments'],
    heroRegister: 'tight',
    shareCopy: 'Your Tesla just respawned.',
    accentToken: '--accent-category-games',
    subcollections: [
      {
        id: 'online-battle-royale',
        label: 'Online & Battle Royale',
        keywords: ['fortnite', 'among us', 'roblox', 'minecraft', 'fall guys', 'battle royale', 'victory royale', 'minecraft', 'apex'],
      },
      {
        id: 'horror-survival',
        label: 'Horror & Survival',
        keywords: ['fnaf', 'five nights', 'freddy', 'jumpscare', 'horror', 'game over', 'survival', 'resident evil'],
      },
      {
        id: 'aaa-franchises',
        label: 'AAA Franchises',
        keywords: ['halo', 'gta', 'grand theft auto', 'call of duty', 'cod', 'battlefield', 'destiny', 'modern warfare', 'wanted'],
      },
    ],
  },
  'tv-shows': {
    slug: 'tv-shows',
    name: 'TV Shows',
    title: 'TV Show Tesla Lock Sounds',
    description: 'TV theme songs, catchphrases, and iconic moments from television, for your Tesla lock.',
    metaDescription: 'Download free TV show lock sounds for your Tesla. Iconic theme songs, catchphrases, and memorable audio from television.',
    heroText: 'Prime Time Every Time',
    icon: 'Monitor',
    relatedCategories: ['movies', 'cartoons-animation', 'music'],
    features: ['Theme songs', 'Famous catchphrases', 'Iconic moments', 'Sitcom classics'],
    heroRegister: 'default',
    shareCopy: 'Prime time every time you lock.',
    accentToken: '--accent-category-default',
  },
  'funny': {
    slug: 'funny',
    name: 'Funny Tesla Lock Sounds',
    title: 'Funny Tesla Lock Sounds: Meme & Comedy Chimes',
    description: 'Vine Boom, Emotional Damage, Bruh: the funniest Tesla lock sounds on the internet. Instant WAV download.',
    metaDescription: 'Download free funny Tesla lock sounds. Meme chimes, comedy audio, and viral sound effects, WAV-ready for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Make the Parking Lot Laugh',
    icon: 'Laugh',
    relatedCategories: ['memes', 'viral', 'internet-culture'],
    features: ['Vine classics', 'TikTok memes', 'Comedy reactions', 'Viral audio'],
    heroRegister: 'tight',
    shareCopy: 'Show your group chat what a Tesla can really do.',
    accentToken: '--accent-category-memes',
  },
  'tesla-ev': {
    slug: 'tesla-ev',
    name: 'Tesla & EV',
    title: 'Tesla & EV Lock Sounds',
    description: 'KITT scanners, Tron grids, Iron Man arc reactors, Transformers morphs: EV-themed lock chimes for your Tesla.',
    metaDescription: 'Download free Tesla- and EV-themed lock sounds. Iron Man, KITT, Tron, Transformers and more, purpose-built for your Tesla.',
    heroText: 'Future-Forward',
    icon: 'Car',
    relatedCategories: ['sci-fi-futuristic', 'retro-tech', 'sound-effects'],
    features: ['EV-themed chimes', 'Sci-fi vehicle sounds', 'Smart-tech effects', 'Tesla-flavored audio'],
    heroRegister: 'wide',
    shareCopy: 'Built for this car, specifically.',
    accentToken: '--accent-category-sci-fi',
  },
  // TES-1834 — the twelve DB categories that had no hub of their own. Together
  // they hold 264 active sounds that were only reachable through the
  // `/sounds?category=` query view, so none of them had an indexable landing
  // page. Every description below names sounds that are actually in that
  // category (verified against prod), per ANTI_SLOP rule 3.
  'anime': {
    slug: 'anime',
    name: 'Anime',
    title: 'Anime Tesla Lock Sounds: Pikachu, Nani & Domain Expansion (Free WAV)',
    description: 'Pikachu, "Nani", Goku\'s Super Saiyan aura, Gojo\'s Domain Expansion, and the Death Note L theme, cut to Tesla-format WAV.',
    metaDescription: 'Download free anime lock sounds for your Tesla. Pikachu, Nani, Dragon Ball, Naruto and Jujutsu Kaisen audio for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Main Character Energy',
    icon: 'Film',
    relatedCategories: ['cartoons-animation', 'games', 'tv-shows'],
    features: ['Shonen power-ups', 'Reaction lines', 'Series themes', 'Pokémon cries'],
    heroRegister: 'tight',
    shareCopy: 'Your Tesla just powered up.',
    accentToken: '--accent-category-animation',
  },
  'adult-animation': {
    slug: 'adult-animation',
    name: 'Adult Animation',
    title: 'Adult Animation Lock Sounds: Family Guy, Simpsons & South Park (Free WAV)',
    description: 'Stewie\'s "Okay bye", Homer\'s D\'oh, Nelson\'s Ha-Ha, Cartman\'s exit line, and Rick and Morty bits, all Tesla-format WAV.',
    metaDescription: 'Free adult animation lock sounds for Tesla. Family Guy, The Simpsons, South Park and Rick and Morty clips for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Prime-Time Cartoons',
    icon: 'Laugh',
    relatedCategories: ['cartoons-animation', 'tv-shows', 'memes'],
    features: ['Family Guy lines', 'Simpsons classics', 'South Park bits', 'Rick and Morty cuts'],
    heroRegister: 'tight',
    shareCopy: 'Okay bye.',
    accentToken: '--accent-category-animation',
  },
  'spongebob-nickelodeon': {
    slug: 'spongebob-nickelodeon',
    name: 'SpongeBob & Nickelodeon',
    title: 'SpongeBob Lock Sounds for Tesla: My Leg, A Few Moments Later (Free WAV)',
    description: '"My Leg", the "A Few Moments Later" card, Patrick screaming, Squidward\'s "this guy stinks", and the sad ukulele.',
    metaDescription: 'Download free SpongeBob and Nickelodeon lock sounds for Tesla. My Leg, A Few Moments Later and Patrick Star audio for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Straight From Bikini Bottom',
    icon: 'Waves',
    relatedCategories: ['cartoons-animation', 'memes', 'tv-shows'],
    features: ['SpongeBob quotes', 'Time-skip cards', 'Patrick panic yells', 'Bikini Bottom stings'],
    heroRegister: 'tight',
    shareCopy: 'A few moments later…',
    accentToken: '--accent-category-animation',
  },
  'jingles-ringtones': {
    slug: 'jingles-ringtones',
    name: 'Jingles & Ringtones',
    title: 'Jingle & Ringtone Lock Sounds for Tesla: Netflix Ta-Dum & More (Free WAV)',
    description: 'The Netflix ta-dum, Taco Bell bong, Old Spice whistle, La Cucaracha horn, and M83\'s Midnight City, in Tesla-format WAV.',
    metaDescription: 'Free jingle and ringtone lock sounds for Tesla. Netflix ta-dum, Taco Bell bong, Old Spice whistle and novelty horns for Model 3, Y, S, X & Cybertruck.',
    heroText: 'The Ta-Dum Treatment',
    icon: 'Bell',
    relatedCategories: ['music', 'retro-tech', 'sound-effects'],
    features: ['Brand stingers', 'Classic ringtones', 'Novelty horns', 'Synth hooks'],
    heroRegister: 'default',
    shareCopy: 'Ta-dum.',
    accentToken: '--accent-category-jingles',
  },
  'hip-hop-r-b': {
    slug: 'hip-hop-r-b',
    name: 'Hip-Hop & R&B',
    title: 'Hip-Hop & R&B Lock Sounds for Tesla: Snoop, Biggie & James Brown (Free WAV)',
    description: 'Snoop Dogg vocal drops, the Big Poppa hook, Nelly\'s "Ahh", Maybach Music, and James Brown\'s "I Feel Good".',
    metaDescription: 'Download free hip-hop and R&B lock sounds for Tesla. Snoop Dogg, Notorious B.I.G., Nelly and James Brown clips for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Pull Up With Bass',
    icon: 'Music',
    relatedCategories: ['music', 'memes', 'sports'],
    features: ['Rap vocal drops', 'Funk & soul hooks', 'Producer tags', 'Meme anthems'],
    heroRegister: 'display',
    shareCopy: 'Pull up like the beat just dropped.',
    accentToken: '--accent-category-music',
  },
  'discord-app-sounds': {
    slug: 'discord-app-sounds',
    name: 'Discord & App Sounds',
    title: 'Discord & App Notification Lock Sounds for Tesla (Free WAV)',
    description: 'Discord join, leave and disconnect boops, the Apple Pay cha-ching, and the Microsoft Teams call chime, as Tesla-format WAV.',
    metaDescription: 'Free app notification lock sounds for Tesla. Discord join, disconnect, Apple Pay and Teams chimes for Model 3, Y, S, X & Cybertruck.',
    heroText: 'You Have One Notification',
    icon: 'MessageCircle',
    relatedCategories: ['retro-tech', 'sound-effects', 'internet-culture'],
    features: ['Discord voice boops', 'Payment confirmations', 'Work-app chimes', 'Notification pings'],
    heroRegister: 'default',
    shareCopy: 'Your car just joined the voice channel.',
    accentToken: '--accent-category-default',
  },
  'vehicles-transport': {
    slug: 'vehicles-transport',
    name: 'Vehicles & Transport',
    title: 'Vehicle & Transport Lock Sounds for Tesla: Horns, Engines & Sirens (Free WAV)',
    description: 'V8 engine revs, train and ferry horns, airplane cabin chimes, and police sirens, cut to Tesla-format WAV.',
    metaDescription: 'Download free vehicle and transport lock sounds for Tesla. Engine revs, train horns, cabin chimes and sirens for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Engines, Horns & Chimes',
    icon: 'Car',
    relatedCategories: ['tesla-ev', 'sound-effects', 'f1-motorsport'],
    features: ['Engine revs', 'Train & boat horns', 'Cabin chimes', 'Emergency sirens'],
    heroRegister: 'wide',
    shareCopy: 'A V8 noise from a car with no engine.',
    accentToken: '--accent-category-default',
  },
  'pro-wrestling-mma': {
    slug: 'pro-wrestling-mma',
    name: 'Pro Wrestling & MMA',
    title: 'Wrestling & MMA Lock Sounds for Tesla: Undertaker Gong, Cena & Buffer (Free WAV)',
    description: 'The Undertaker\'s gong, John Cena\'s entrance, Bruce Buffer\'s "It\'s time", Macho Man\'s "Oh yeah", and Stone Cold\'s "Hell yeah".',
    metaDescription: 'Free wrestling and MMA lock sounds for Tesla. Undertaker gong, John Cena entrance and UFC announcer calls for Model 3, Y, S, X & Cybertruck.',
    heroText: 'And His Music Hits',
    icon: 'Trophy',
    relatedCategories: ['sports', 'tv-shows', 'memes'],
    features: ['Entrance themes', 'Ring bells', 'Announcer calls', 'Catchphrases'],
    heroRegister: 'urgent',
    shareCopy: 'And his music hits.',
    accentToken: '--accent-category-alarms',
  },
  'soccer-football': {
    slug: 'soccer-football',
    name: 'Soccer & Football',
    title: 'Soccer Lock Sounds for Tesla: SIUUU, Champions League & Goal Calls (Free WAV)',
    description: 'Ronaldo\'s SIUUU, the UEFA Champions League anthem, Brazilian goal commentary, and Haaland terrace chants.',
    metaDescription: 'Download free soccer lock sounds for Tesla. SIUUU, Champions League anthem and goal commentary for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Celebrate Every Parking Job',
    icon: 'Trophy',
    relatedCategories: ['sports', 'memes', 'music'],
    features: ['Goal celebrations', 'Stadium chants', 'Commentator calls', 'Competition anthems'],
    heroRegister: 'urgent',
    shareCopy: 'SIUUU.',
    accentToken: '--accent-category-alarms',
  },
  'game-shows-quiz-tv': {
    slug: 'game-shows-quiz-tv',
    name: 'Game Shows & Quiz TV',
    title: 'Game Show Lock Sounds for Tesla: Jeopardy, Millionaire & Price Is Right (Free WAV)',
    description: 'The Jeopardy! thinking-time theme, the Daily Double sting, Millionaire\'s final-answer tension, and the Price Is Right losing horn.',
    metaDescription: 'Free game show lock sounds for Tesla. Jeopardy theme, Daily Double, Millionaire stings and fail horns for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Is That Your Final Answer',
    icon: 'Monitor',
    relatedCategories: ['tv-shows', 'memes', 'sound-effects'],
    features: ['Thinking-time themes', 'Correct-answer dings', 'Fail horns', 'Suspense stings'],
    heroRegister: 'default',
    shareCopy: 'Is that your final answer?',
    accentToken: '--accent-category-retro',
  },
  'f1-motorsport': {
    slug: 'f1-motorsport',
    name: 'F1 & Motorsport',
    title: 'F1 Lock Sounds for Tesla: Team Radio, Engine Flybys & Race Themes (Free WAV)',
    description: '"Box box box" team radio, Verstappen\'s "simply lovely", Leclerc\'s "I am stupid", Le Mans flybys, and the Formula 1 theme.',
    metaDescription: 'Download free F1 and motorsport lock sounds for Tesla. Team radio calls, engine flybys and race themes for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Box, Box, Box',
    icon: 'Car',
    relatedCategories: ['tesla-ev', 'sports', 'vehicles-transport'],
    features: ['Team radio calls', 'Engine flybys', 'Race-start themes', 'Driver quotes'],
    heroRegister: 'urgent',
    shareCopy: 'Box, box, box.',
    accentToken: '--accent-category-alarms',
  },
  'sci-fi-action-films': {
    slug: 'sci-fi-action-films',
    name: 'Sci-Fi & Action Films',
    title: 'Sci-Fi & Action Movie Lock Sounds for Tesla: Bond, Jurassic Park & Predator (Free WAV)',
    description: 'The James Bond riff, the Jurassic Park gates fanfare, the X-Men title theme, a Tusken Raider cry, and Predator\'s cloak shimmer.',
    metaDescription: 'Free sci-fi and action movie lock sounds for Tesla. James Bond, Jurassic Park, X-Men and Predator audio for Model 3, Y, S, X & Cybertruck.',
    heroText: 'Cue the Title Card',
    icon: 'Rocket',
    relatedCategories: ['movies', 'sci-fi-futuristic', 'tv-shows'],
    features: ['Title fanfares', 'Spy themes', 'Creature effects', 'Action stings'],
    heroRegister: 'wide',
    shareCopy: 'Roll the opening credits.',
    accentToken: '--accent-category-sci-fi',
  },
}

export function getCategoryBySlug(slug: string): CategoryInfo | undefined {
  const lower = slug.toLowerCase()
  if (categoryData[lower]) return categoryData[lower]
  // Dynamic fallback for categories in the DB that don't yet have a categoryData entry.
  // Generates a usable CategoryInfo from the slug so the page renders instead of 404ing.
  const name = slugToCategoryName(lower)
  return {
    slug: lower,
    name,
    title: `${name} Tesla Lock Sounds`,
    description: `Browse free ${name.toLowerCase()} lock sounds for your Tesla. Download WAV-ready custom sounds for Model 3, Y, S, X & Cybertruck.`,
    metaDescription: `Download free ${name.toLowerCase()} lock sounds for your Tesla. Custom audio for your vehicle.`,
    heroText: name,
    icon: 'Zap',
    relatedCategories: [],
    features: [],
  }
}

export function getAllCategorySlugs(): string[] {
  return Object.keys(categoryData)
}

// Map database category names to URL slugs
export function categoryNameToSlug(name: string): string {
  return name.toLowerCase().replace(/\s*&\s*/g, '-').replace(/\s+/g, '-')
}

/**
 * Link a database category to a curated landing page when one exists. Fine-
 * grained catalog categories without dedicated editorial metadata fall back to
 * the library's category query instead of sending users to a 404 route.
 */
export function categoryBrowseHref(name: string): string {
  const slug = categoryNameToSlug(name)
  return categoryData[slug]
    ? `/sounds/category/${slug}`
    : `/sounds?category=${encodeURIComponent(name)}`
}

// Map URL slugs to database category names
export function slugToCategoryName(slug: string): string {
  const category = categoryData[slug]
  return category?.name || slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

// Special slug-to-dB-name mappings for categories whose URL slug
// doesn't naively title-case back to the exact database name.
// Keep this in sync with the DB category list.
const slugToDbNameOverrides: Record<string, string> = {
  'nature-animals':    'Nature & Animals',
  'sound-effects':     'Sound Effects',
  'horror-spooky':     'Horror & Spooky',
  'sci-fi-futuristic': 'Sci-Fi & Futuristic',
  'internet-culture':  'Internet Culture',
  'cartoons-animation':'Cartoons & Animation',
  'retro-gaming':      'Retro Gaming',
  'modern-gaming':     'Modern Gaming',
  'tv-shows':          'TV Shows',
  'tesla-ev':          'Tesla & EV',
  // DB category is "Classic Memes"; both the canonical slug and the funny alias target it
  'memes':             'Classic Memes',
  'funny':             'Classic Memes',
  'vehicles-transport':'Vehicles & Transport',
  // No "Reactions" DB category; surface Internet Culture sounds (closest match)
  'reactions':         'Internet Culture',
  // TES-1834 hubs. Listed explicitly rather than leaning on
  // categoryData[slug].name: `name` is user-facing display text, so a future
  // copy tweak there would silently re-point the DB query and empty the page.
  'anime':                 'Anime',
  'adult-animation':       'Adult Animation',
  'spongebob-nickelodeon': 'SpongeBob & Nickelodeon',
  'jingles-ringtones':     'Jingles & Ringtones',
  'hip-hop-r-b':           'Hip-Hop & R&B',
  'discord-app-sounds':    'Discord & App Sounds',
  'pro-wrestling-mma':     'Pro Wrestling & MMA',
  'soccer-football':       'Soccer & Football',
  'game-shows-quiz-tv':    'Game Shows & Quiz TV',
  'f1-motorsport':         'F1 & Motorsport',
  'sci-fi-action-films':   'Sci-Fi & Action Films',
}

export function slugToDbCategoryName(slug: string): string {
  return slugToDbNameOverrides[slug] || slugToCategoryName(slug)
}

// Umbrella pages whose sounds span several fine-grained DB categories. The
// catalog curation split broad buckets (e.g. "Games") into specific ones
// ("Modern Gaming", "Mario & Nintendo", …) — without this union, the /games
// page queried the literal "Games" category and showed 3 straggler sounds.
// Keep in sync with the DB category list (select distinct category from sounds).
const slugToDbNamesMulti: Record<string, string[]> = {
  games: [
    'Modern Gaming',
    'Retro Gaming',
    'Mario & Nintendo',
    'FPS & Battle Royale',
    'Classic Arcade & Fighting',
    'Mobile & Casual Games',
    'Minecraft & Sandbox',
  ],
  memes: ['Classic Memes', 'International Memes'],
  funny: ['Classic Memes', 'Comedy Sound Effects'],
}

/**
 * Slugs served by download-velocity ranking (the nightly-computed
 * trending_sounds table), NOT a DB category. Their page copy is explicitly
 * "most downloaded / trending" and no DB category carries these names (a
 * literal `category = 'Trending'`/`'Viral'` query returns 0 rows), so both the
 * category page (app/sounds/category/[slug]/page.tsx) and the category-health
 * tripwire (lib/category-health.ts) must treat them as velocity pages instead
 * of resolving them through slugToDbCategoryNames.
 */
export const VELOCITY_SLUGS = new Set<string>(['trending', 'viral'])

/**
 * DB category name(s) for a category-page slug. Most slugs map to one DB
 * category; umbrella slugs (games, memes, funny) return the union of their
 * fine-grained DB categories. Pass the result straight to
 * soundsApi.getSounds({ category }) — it accepts string | string[].
 */
export function slugToDbCategoryNames(slug: string): string | string[] {
  return slugToDbNamesMulti[slug] ?? slugToDbCategoryName(slug)
}
